import { lockScroll, unlockScroll } from '../scroll-lock';
import { loadHubspot } from './hubspot-loader';
import { closeNavMenu } from '../nav';

// Consultation popup (modal), promo popup (corner card) and the floating button.
const PROMO_KEY = 'armada-promo-seen';
const DAY = 24 * 60 * 60 * 1000;
const GAPS: Record<string, number> = { day: DAY, week: 7 * DAY, once: Infinity };

function bindClose(dialog: HTMLDialogElement): void {
	dialog.querySelectorAll('[data-armada-popup-close]').forEach((el) => {
		el.addEventListener('click', () => dialog.close());
	});
}

function initConsult(): void {
	const dialog = document.querySelector<HTMLDialogElement>('#consultation-popup');

	if (!dialog) {
		return;
	}

	bindClose(dialog);

	// Start fetching the form as soon as the pointer or focus reaches a trigger.
	const warm = (event: Event): void => {
		if ((event.target as HTMLElement).closest?.('a[href="#consultation-popup"]')) {
			loadHubspot(dialog);
		}
	};

	document.addEventListener('pointerover', warm, { passive: true });
	document.addEventListener('focusin', warm);

	document.addEventListener('click', (event) => {
		const link = (event.target as HTMLElement).closest<HTMLAnchorElement>('a[href="#consultation-popup"]');

		if (!link || dialog.open) {
			return;
		}

		event.preventDefault();
		loadHubspot(dialog);
		// The link can sit in the mobile menu, which holds its own scroll lock.
		closeNavMenu();
		dialog.showModal();
		lockScroll();
	});

	// A click on the backdrop lands on the dialog itself, outside its body.
	dialog.addEventListener('click', (event) => {
		if (event.target === dialog) {
			dialog.close();
		}
	});

	dialog.addEventListener('close', () => unlockScroll());
}

// Whether the promo was already shown within the chosen interval.
function promoSeen(frequency: string): boolean {
	try {
		if ('always' === frequency) {
			return false;
		}

		if ('session' === frequency) {
			return sessionStorage.getItem(PROMO_KEY) !== null;
		}

		const seen = Number(localStorage.getItem(PROMO_KEY));

		return seen > 0 && Date.now() - seen < (GAPS[frequency] ?? DAY);
	} catch {
		return false;
	}
}

function rememberPromo(frequency: string): void {
	try {
		if ('session' === frequency) {
			sessionStorage.setItem(PROMO_KEY, '1');
		} else if ('always' !== frequency) {
			localStorage.setItem(PROMO_KEY, String(Date.now()));
		}
	} catch {
		// Private mode: the popup just shows again next time.
	}
}

function initPromo(): void {
	const dialog = document.querySelector<HTMLDialogElement>('[data-armada-promo]');

	if (!dialog) {
		return;
	}

	const frequency = dialog.dataset.frequency || 'day';

	if (promoSeen(frequency)) {
		return;
	}

	bindClose(dialog);

	dialog.addEventListener('close', () => {
		document.documentElement.classList.remove('armada-promo-open');
	});

	window.setTimeout(() => {
		// Not over an open menu or the consultation popup.
		if (document.documentElement.classList.contains('armada-scroll-locked')) {
			return;
		}

		dialog.show();
		document.documentElement.classList.add('armada-promo-open');
		rememberPromo(frequency);
	}, Math.max(0, Number(dialog.dataset.delay) || 0) * 1000);
}

// The floating button steps aside once the footer (and its menus) is on screen.
function initFloatingButton(): void {
	const button = document.querySelector<HTMLElement>('.armada-float-cta');
	const footer = document.querySelector<HTMLElement>('.armada-footer');

	if (!button || !footer) {
		return;
	}

	new IntersectionObserver(([entry]) => {
		button.classList.toggle('is-hidden', entry.isIntersecting);
	}).observe(footer);
}

export default function initPopups(): void {
	initConsult();
	initPromo();
	initFloatingButton();
}
